import { onBeforeUnmount } from 'vue'
import type { GalleryItem } from 'lightgallery/lg-utils'
import type { LightGallery } from 'lightgallery/lightgallery'

type MediaGalleryItem = {
  url: string
  previewUrl?: string
  alt?: string | null
  isVideo?: boolean
}

type LightGalleryFactory = typeof import('lightgallery')['default']
type VideoPlugin = typeof import('lightgallery/plugins/video')['default']

let assetsPromise:
  | Promise<{
      lightGallery: LightGalleryFactory
      videoPlugin: VideoPlugin
    }>
  | null = null

const loadGalleryAssets = async (): Promise<{
  lightGallery: LightGalleryFactory
  videoPlugin: VideoPlugin
}> => {
  if (!assetsPromise) {
    assetsPromise = Promise.all([
      import('lightgallery'),
      import('lightgallery/plugins/video'),
      import('lightgallery/css/lightgallery.css'),
    ]).then(([lightGalleryModule, videoPluginModule]) => ({
      lightGallery: lightGalleryModule.default,
      videoPlugin: videoPluginModule.default,
    }))
  }

  return assetsPromise
}

export const toGalleryItems = (items: MediaGalleryItem[]): GalleryItem[] =>
  items
    .filter((item) => Boolean(item.url))
    .map((item) => ({
      src: item.url,
      thumb: item.previewUrl || item.url,
      alt: item.alt ?? undefined,
      ...(item.isVideo ? { iframe: true as const } : {}),
    }))

export const useSharedMediaGallery = () => {
  let container: HTMLElement | null = null
  let instance: LightGallery | null = null

  const ensureInit = async (): Promise<LightGallery | null> => {
    if (instance) return instance
    if (typeof document === 'undefined') return null

    const { lightGallery, videoPlugin } = await loadGalleryAssets()

    if (instance) return instance

    container = document.createElement('div')
    container.className = 'shared-media-gallery-root'
    container.setAttribute('aria-hidden', 'true')
    container.style.cssText =
      'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;pointer-events:none;visibility:hidden;'
    document.body.appendChild(container)

    instance = lightGallery(container, {
      dynamic: true,
      dynamicEl: [],
      speed: 500,
      download: false,
      plugins: [videoPlugin],
      container: document.body,
    } as Parameters<LightGalleryFactory>[1])

    return instance
  }

  const prewarm = async (): Promise<void> => {
    await ensureInit()
  }

  const openGallery = async (items: GalleryItem[], index = 0): Promise<void> => {
    if (!items.length) return

    const gallery = await ensureInit()
    if (!gallery) return

    gallery.refresh(items)
    gallery.openGallery(index)
  }

  const destroy = (): void => {
    if (instance) {
      try {
        instance.destroy()
      } catch {
        // Ignore cleanup failures from third-party gallery teardown.
      }
      instance = null
    }

    if (container?.parentNode) {
      container.parentNode.removeChild(container)
    }

    container = null
  }

  onBeforeUnmount(destroy)

  return {
    destroy,
    openGallery,
    prewarm,
  }
}
